'use client';
import { DesktopNavbar } from './desktop-navbar';
import { MobileNavbar } from './mobile-navbar';
import { motion } from 'framer-motion';
import { Background } from '@/components/background';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export function NavBar({ landingMode = false }: { landingMode?: boolean } = {}) {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const isInitialPageLoad = !location.key; // location.key only exists after navigation

  // Create translated nav items that update when language changes
  const [navItems, setNavItems] = useState([
    {
      title: t('nav.about'),
      link: '/about',
    },
    {
      title: t('nav.careers'),
      link: '/careers',
    },
    {
      title: t('nav.contact'),
      link: '/contact',
    },
  ]);

  // Force scroll to top on page changes
  useEffect(() => {
    // Immediately reset scroll position when pathname changes
    window.scrollTo(0, 0);

    // For browsers that might be slow to respond
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, [location.pathname]);

  // Update navigation items whenever language changes
  useEffect(() => {
    setNavItems([
      {
        title: t('nav.about'),
        link: '/about',
      },
      {
        title: t('nav.careers'),
        link: '/careers',
      },
      {
        title: t('nav.contact'),
        link: '/contact',
      },
    ]);
  }, [t, i18n.language]);

  // Listen for custom language change events
  useEffect(() => {
    const handleLanguageChange = () => {
      setNavItems([
        {
          title: t('nav.about'),
          link: '/about',
        },
        {
          title: t('nav.careers'),
          link: '/careers',
        },
        {
          title: t('nav.contact'),
          link: '/contact',
        },
      ]);
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [t]);

  return (
    <motion.nav
      // No animations during navigation - start already visible
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      // No layoutId to prevent animation preservation across routes
      className="max-w-7xl fixed top-4 mx-auto inset-x-0 z-50 w-[95%] lg:w-full"
      style={{ position: 'fixed' }} // Explicitly set position for scroll calculation
    >
      <div className="hidden lg:block w-full relative z-10">
        <DesktopNavbar navItems={landingMode ? [] : navItems} landingMode={landingMode} />
      </div>
      <div className="flex h-full w-full items-center lg:hidden relative z-10">
        <MobileNavbar navItems={landingMode ? [] : navItems} landingMode={landingMode} />
      </div>
    </motion.nav>
  );
}
